/**
 * CrmContactPicker
 * ----------------
 * Shared modal that lists CRM contacts (flattened from `crm_accounts[].contacts`)
 * with department filter + search + multi-select. Includes a "+ Add new
 * lead/contact" action that opens an inline form which writes into CRM via
 * `POST /api/crm/accounts` (and optionally appends a contact) — then re-loads
 * the picker so the freshly-added person appears at the top of the list.
 *
 * Used by:
 *   - EmailCampaigns.jsx — to pick campaign recipients
 *   - AdminBackordersBoard.jsx — to attach a customer to a new manual backorder
 */

import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Search, RefreshCw, UserPlus, Loader2, X, Mail, Users, Filter, ChevronRight,
} from "lucide-react";
import { API } from "@/App";
import HospitalQuickAdd from "./HospitalQuickAdd";

const DEFAULT_DEPARTMENTS = [
  "Supply Chain",
  "Procurement",
  "Materials Management",
  "EVS",
  "Janitorial",
  "OR / Surgical",
  "Infection Prevention",
  "Facilities",
  "Pharmacy",
  "Lab",
  "Administration",
  "Other",
];

export default function CrmContactPicker({
  open,
  onOpenChange,
  // mode: "multi" → returns array of selected contacts.
  // mode: "single" → returns one selected contact and closes.
  mode = "multi",
  onConfirm,                  // (contacts: [{name, email, account_id, contact_id, department, ...}]) => void
  confirmLabel = "Use selected",
  title = "Select hospital contacts",
  // When the caller knows which hospital it is working (the Sourcing Desk
  // always does), the picker can add contacts and locations to that network
  // permanently instead of only reading from it.
  hospitalId = "",
  hospitalName = "",
}) {
  const [loading, setLoading] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [search, setSearch] = useState("");
  const [deptFilter, setDeptFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all"); // all | lead | customer
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const [showAddLead, setShowAddLead] = useState(false);
  const [hospitalFilter, setHospitalFilter] = useState("all");

  const token = typeof localStorage !== "undefined" ? localStorage.getItem("sterilemate_token") : null;
  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (deptFilter !== "all") params.department = deptFilter;
      if (statusFilter !== "all") params.status = statusFilter;
      if (search.trim()) params.q = search.trim();
      const r = await axios.get(`${API}/crm-campaign-contacts`, { headers, params });
      setContacts(r.data.contacts || []);
      const allDepts = Array.from(new Set([...(r.data.departments || []), ...DEFAULT_DEPARTMENTS]))
        .filter(Boolean)
        .sort();
      setDepartments(allDepts);
    } catch {
      toast.error("Failed to load CRM contacts");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (!open) return;
    // Search is debounced; department/status refetch immediately. Without the
    // search term in here, typing in the box did nothing until you touched a
    // filter — the list just sat there showing everybody.
    const t = setTimeout(load, search.trim() ? 300 : 0);
    return () => clearTimeout(t);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [open, deptFilter, statusFilter, search]);

  const hospitals = useMemo(() => {
    const map = new Map();
    for (const c of contacts) {
      const id = c.account_id || c.health_system_id;
      const name = c.account_name;
      if (id && name && !map.has(id)) map.set(id, { id, name });
    }
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [contacts]);

  const visibleContacts = useMemo(() => {
    if (hospitalFilter === "all") return contacts;
    return contacts.filter((c) => (c.account_id || c.health_system_id) === hospitalFilter);
  }, [contacts, hospitalFilter]);

  const filteredCount = visibleContacts.length;
  const allKeys = useMemo(() => visibleContacts.map((c) => `${c.account_id}::${c.email}`), [visibleContacts]);
  const allSelected = allKeys.length > 0 && allKeys.every((k) => selectedKeys.has(k));

  const toggleAll = () => {
    if (allSelected) {
      setSelectedKeys(new Set());
    } else {
      setSelectedKeys(new Set(allKeys));
    }
  };

  const toggleOne = (key, c) => {
    if (mode === "single") {
      onConfirm?.([c]);
      onOpenChange(false);
      return;
    }
    const next = new Set(selectedKeys);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setSelectedKeys(next);
  };

  const confirm = () => {
    const picked = visibleContacts.filter((c) => selectedKeys.has(`${c.account_id}::${c.email}`));
    onConfirm?.(picked);
    onOpenChange(false);
    setSelectedKeys(new Set());
  };

  const onLeadAdded = async (created) => {
    setShowAddLead(false);
    toast.success(`Added: ${created.name}`);
    await load();
    // Auto-select the newly created contact (match by primary email)
    if (created.email) {
      const k = `${created.id}::${(created.email || "").toLowerCase()}`;
      if (mode === "single") {
        onConfirm?.([{
          account_id: created.id, contact_id: created.id, name: created.name,
          email: (created.email || "").toLowerCase(),
          account_name: created.name, department: created.category || "Other",
        }]);
        onOpenChange(false);
        return;
      }
      const next = new Set(selectedKeys);
      next.add(k);
      setSelectedKeys(next);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="crm-picker-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-amber-600" /> {title}
            </DialogTitle>
          </DialogHeader>

          {/* Toolbar */}
          <div className="flex flex-wrap items-center gap-2 pb-3 border-b border-slate-200">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Search by name, email, hospital…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && load()}
                className="pl-9 h-9"
                data-testid="crm-picker-search"
              />
            </div>
            <select
              value={hospitalFilter}
              onChange={(e) => setHospitalFilter(e.target.value)}
              className="h-9 border border-slate-300 rounded-md px-2 text-sm max-w-[180px]"
              data-testid="crm-picker-hospital"
              title="Filter by hospital / account"
            >
              <option value="all">All hospitals</option>
              {hospitals.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
            </select>
            <select
              value={deptFilter}
              onChange={(e) => setDeptFilter(e.target.value)}
              className="h-9 border border-slate-300 rounded-md px-2 text-sm"
              data-testid="crm-picker-dept"
            >
              <option value="all">All departments</option>
              {departments.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="h-9 border border-slate-300 rounded-md px-2 text-sm"
              data-testid="crm-picker-status"
            >
              <option value="all">All</option>
              <option value="customer">Customers</option>
              <option value="lead">Leads</option>
            </select>
            <Button size="sm" variant="outline" onClick={load} disabled={loading} className="gap-1.5">
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            </Button>
            <Button size="sm" onClick={() => setShowAddLead(true)} className="gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="crm-picker-add-lead">
              <UserPlus className="w-3.5 h-3.5" /> Add new lead
            </Button>
          </div>

          {hospitalId ? (
            <div className="pt-2">
              <HospitalQuickAdd hospitalId={hospitalId} hospitalName={hospitalName || "this hospital"}
                onAdded={(res) => { if (res?.type === "contact") load(); }} />
            </div>
          ) : null}

          {mode === "multi" && filteredCount > 0 && (
            <div className="flex items-center justify-between py-2 px-1 text-xs border-b border-slate-200">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={allSelected} onChange={toggleAll} className="h-4 w-4 rounded" />
                <span className="font-semibold">
                  {allSelected ? "Deselect all" : `Select all ${filteredCount}`}
                </span>
              </label>
              <span className="text-slate-500">{selectedKeys.size} selected</span>
            </div>
          )}

          {/* List */}
          <div className="flex-1 overflow-y-auto -mx-2 px-2">
            {loading && <div className="py-10 text-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Loading…</div>}
            {!loading && visibleContacts.length === 0 && (
              <div className="py-10 text-center text-slate-500">
                <Users className="w-10 h-10 mx-auto text-slate-300 mb-2" />
                <p className="text-sm">No contacts match. Try clearing filters or click <strong>Add new lead</strong>.</p>
              </div>
            )}
            <div className="space-y-1.5">
              {visibleContacts.map((c) => {
                const key = `${c.account_id}::${c.email}`;
                const checked = selectedKeys.has(key);
                return (
                  <div
                    key={key}
                    onClick={() => toggleOne(key, c)}
                    className={`flex items-start gap-3 p-2.5 rounded-lg border cursor-pointer transition-colors ${checked ? "bg-amber-50 border-amber-300" : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"}`}
                    data-testid={`crm-picker-row-${c.account_id}`}
                  >
                    {mode === "multi" && (
                      <input type="checkbox" readOnly checked={checked} className="mt-1 h-4 w-4 rounded" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-bold text-slate-900">{c.name || "(no name)"}</span>
                        {c.title && <span className="text-xs text-slate-500">· {c.title}</span>}
                        {c.is_portal_user && (
                          <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-blue-100 text-blue-700" title="Has a login on the hospital portal">
                            PORTAL USER
                          </span>
                        )}
                        {c.account_status && (
                          <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${c.account_status === "customer" ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-blue-700"}`}>
                            {c.account_status}
                          </span>
                        )}
                        {c.department && (
                          <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-violet-100 text-violet-700">
                            {c.department}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-600 mt-0.5 flex items-center gap-2">
                        <Mail className="w-3 h-3" />
                        {c.email}
                      </div>
                      <div className="text-[11px] text-slate-500 mt-0.5">{c.account_name}</div>
                      {c.last_emailed_at && (
                        <div className="text-[10px] text-slate-400 mt-0.5">
                          Last emailed {new Date(c.last_emailed_at).toLocaleDateString()}{c.last_emailed_campaign ? ` · ${c.last_emailed_campaign}` : ""}
                        </div>
                      )}
                    </div>
                    {mode === "single" && <ChevronRight className="w-4 h-4 text-slate-400 mt-1" />}
                  </div>
                );
              })}
            </div>
          </div>

          <DialogFooter className="border-t border-slate-200 pt-3">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            {mode === "multi" && (
              <Button
                onClick={confirm}
                disabled={selectedKeys.size === 0}
                className="bg-amber-600 hover:bg-amber-700 gap-1.5"
                data-testid="crm-picker-confirm"
              >
                {confirmLabel} ({selectedKeys.size})
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CrmQuickAddLead open={showAddLead} onOpenChange={setShowAddLead} onCreated={onLeadAdded} />
    </>
  );
}

// ----------------------------------------------------------------------------
// Inline "Add New Lead" dialog — minimal version of CRM new-account form
// ----------------------------------------------------------------------------
function CrmQuickAddLead({ open, onOpenChange, onCreated, defaultCategory }) {
  const [form, setForm] = useState({
    name: "", contact_name: "", email: "", phone: "", title: "",
    category: defaultCategory || "Supply Chain", status: "lead", type: "hospital", notes: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        name: "", contact_name: "", email: "", phone: "", title: "",
        category: defaultCategory || "Supply Chain", status: "lead", type: "hospital", notes: "",
      });
    }
  }, [open, defaultCategory]);

  const token = typeof localStorage !== "undefined" ? localStorage.getItem("sterilemate_token") : null;
  const headers = { Authorization: `Bearer ${token}` };

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Hospital / account name is required"); return; }
    if (!form.email.trim()) { toast.error("Email is required"); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        type: form.type,
        status: form.status,
        email: form.email.trim().toLowerCase(),
        phone: form.phone || undefined,
        category: form.category,
        notes: form.notes || undefined,
        contacts: form.contact_name ? [{
          name: form.contact_name,
          email: form.email.trim().toLowerCase(),
          phone: form.phone || undefined,
          title: form.title || undefined,
          department: form.category,
          is_primary: true,
        }] : [],
      };
      const r = await axios.post(`${API}/crm/accounts`, payload, { headers });
      const acct = r.data.account || r.data;
      onCreated?.({
        id: acct.id,
        name: payload.name,
        email: payload.email,
        category: form.category,
      });
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to add lead");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="crm-quick-add-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-emerald-600" /> Add new lead
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-1">Hospital / Account *</label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Memorial Hermann" data-testid="quick-add-account-name" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-1">Contact name</label>
              <Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} placeholder="April Williams" data-testid="quick-add-contact-name" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-1">Title</label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Procurement Manager" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-1">Email *</label>
              <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="april@hospital.org" type="email" data-testid="quick-add-email" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-1">Phone</label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="(281) 555-0123" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-1">Department / Category</label>
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full h-9 border border-slate-300 rounded-md px-2 text-sm"
                data-testid="quick-add-category"
              >
                {DEFAULT_DEPARTMENTS.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-1">Status</label>
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full h-9 border border-slate-300 rounded-md px-2 text-sm">
                <option value="lead">Lead</option>
                <option value="customer">Customer</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-1">Notes</label>
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="How did you meet them? What do they need?" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 gap-1.5" data-testid="quick-add-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
            Save lead
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export { CrmQuickAddLead };
